import { useEffect } from 'react';
import { Button, IconButton, useStyles2 } from '@grafana/ui';
import { css } from '@emotion/css';
import type { ChatContextItem } from '../../context/types';
import { useAssistant } from '../../hook';
import type { GrafanaTheme2 } from '@grafana/data';
import type { OpenAssistantProps } from '../../sidebar';
import { reportInteraction } from '@grafana/runtime';

export interface OpenAssistantButtonProps {
  /** Prompt to pass to the openAssistant function. */
  prompt: string;
  /** Origin of the request that opened the assistant. This is used to track the source of the request. Should be a structured string using forward slashes, with the first part as a namespace. Examples: 'grafana-datasources/prometheus/query-builder', 'grafana-slo-app/slo-editor-overview', 'grafana/trace-view-analyzer`. */
  origin: string;
  /** Context to pass to the openAssistant function. Optional, defaults to undefined. Created with `createAssistantContextItem`. */
  context?: ChatContextItem[];
  /** Whether to automatically send the prompt. Optional, defaults to true. */
  autoSend?: boolean;
  /** Text to display on the button. Optional, defaults to 'Analyze with Assistant' */
  title?: string;
  /** Button size, defaults to sm */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** If true, the button will be Assistant icon only with name as title. Defaults to false. */
  iconOnlyButton?: boolean;
  /** Function to call when the button is clicked. Optional, defaults to undefined. */
  onClick?: () => void;
}

/**
 * A button component that opens the Grafana Assistant with configurable prompt and context.
 */
export function OpenAssistantButton(props: OpenAssistantButtonProps) {
  // If the assistant is not available, return empty
  const { isAvailable, openAssistant } = useAssistant();
  if (!isAvailable || !openAssistant) {
    return null;
  }
  return <OpenAssistantButtonView {...props} openAssistant={openAssistant} />;
}

/**
 * Presentational component separated from OpenAssistantButton to avoid hook dependencies in Storybook.
 * It is not exported for users of the package.
 */
export function OpenAssistantButtonView({
  prompt,
  origin,
  context,
  autoSend = true,
  title = 'Analyze with Assistant',
  size = 'sm',
  iconOnlyButton = false,
  openAssistant,
  onClick,
}: OpenAssistantButtonProps & {
  openAssistant: (props: OpenAssistantProps) => void;
}) {
  const styles = useStyles2(getStyles);

  useEffect(() => {
    reportInteraction('grafana_assistant_app_open_sidebar_button_displayed', {
      from: origin,
    });
  }, [origin]);

  return (
    <>
      {iconOnlyButton ? (
        <IconButton
          name="ai-sparkle"
          onClick={() => {
            onClick?.();
            openAssistant({ prompt, context, autoSend, origin });
          }}
          variant="secondary"
          size={size}
          aria-label={title}
          className={styles.icon}
          tooltip={title}
          data-testid="assistant-icon-button"
        />
      ) : (
        <Button
          icon="ai-sparkle"
          onClick={() => {
            onClick?.();
            openAssistant({ prompt, context, autoSend, origin });
          }}
          variant="secondary"
          fill="solid"
          size={size}
          title={title}
          aria-label={title}
          className={styles.button}
          data-testid="assistant-button"
        >
          {title}
        </Button>
      )}
    </>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  const baseBackground = theme.colors.secondary.main;
  const elevatedBackground = theme.colors.emphasize(baseBackground, 0.05);

  // Canvas is what's typically underneath buttons
  const underlyingColor = theme.colors.background.canvas;

  const borderWidth = 1;
  const outerRadius = theme.shape.radius.default;
  // inner radius = outer radius - border width, but take at least 1px
  const innerRadius = `max(calc(${outerRadius} - ${borderWidth}px), 1px)`;

  // Gradient border layer (::before)
  const gradientBorderLayer = {
    content: '""',
    position: 'absolute' as const,
    inset: 0,
    borderRadius: outerRadius,
    cornerShape: 'squircle',
    background: 'linear-gradient(90deg, rgb(168, 85, 247), rgb(249, 115, 22))',
    zIndex: -2,
    pointerEvents: 'none' as const,
  };

  // Solid background layer factory (::after)
  // Layer transparent color over canvas to make it opaque and mask the gradient properly
  const solidBackgroundLayer = (background: string) => ({
    content: '""',
    position: 'absolute' as const,
    inset: `${borderWidth}px`,
    borderRadius: innerRadius,
    cornerShape: 'squircle',
    background: `linear-gradient(${background}, ${background}), ${underlyingColor}`,
    zIndex: -1,
    transition: 'none',
    pointerEvents: 'none' as const,
  });

  return {
    button: css({
      label: 'assistant-button',
      width: 'fit-content',
      maxWidth: '100%',
      position: 'relative',
      isolation: 'isolate',
      border: 'none',
      transition: 'none !important',
      '&::before': gradientBorderLayer,
      '&::after': {
        ...solidBackgroundLayer(baseBackground),
        transition: 'none',
      },
      '&:hover': {
        transition: 'none !important',
      },
      '&:hover::after': {
        background: `linear-gradient(${elevatedBackground}, ${elevatedBackground}), ${underlyingColor}`,
      },
    }),
    icon: css({
      label: 'assistant-icon',
      position: 'relative',
      isolation: 'isolate',
      border: 'none',
      background: 'none',
      padding: theme.spacing(0.5),
      '&::before': { ...gradientBorderLayer, transition: 'background 0.1s ease' },
      '&:hover::after': {
        ...solidBackgroundLayer(baseBackground),
        background: `linear-gradient(${elevatedBackground}, ${elevatedBackground}), ${underlyingColor}`,
      },
    }),
  };
};
